/**
 * Pool Service
 * Handles all pool-related API calls for borrowers, investors, and reviewers
 */

import { DeleteOfferPayload, OfferPayload, RespondToOfferPayload } from '@/types/offer';
import { PaginationMeta } from '@/types/pagination';
import { PoolDetail, PoolFilters, PoolListItem, PoolOverviewData } from '@/types/pool';
import { CreatePoolPayload, DraftPoolListItem, UpdatePoolPayload, UpsertDraftPoolPayload } from '@/types/pool/create';
import { apiClient } from './apiClient';

/** Response from POST /api/pools/favorites/<pool_id> (AddFavoritePoolResponseSchema) */
export type AddFavoritePoolResponse = {
  message: string;
  pool: PoolDetail;
};

type GetPoolsResponse = {
  pools: PoolListItem[];
  pagination: PaginationMeta;
};

type GetDraftPoolsResponse = {
  draft_pools: DraftPoolListItem[];
};

type GetPoolByIdResponse = {
  pool: PoolDetail;
};

type GetOverviewResponse = {
  overview: Partial<PoolOverviewData>;
};

export const poolService = {
  /**
   * Get all pools (for borrowers - their own pools)
   */
  async getPools(page = 1, filters: PoolFilters = {}) {
    const stringFilters: Record<string, string> = {};

    // Convert all filter values to strings for URLSearchParams
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        stringFilters[key] = String(value);
      }
    });

    const params = new URLSearchParams({ page: String(page), ...stringFilters });
    return apiClient<GetPoolsResponse>(`/api/pools?${params}`);
  },

  /**
   * Get draft pools (borrower only)
   */
  async getDraftPools() {
    return apiClient<GetDraftPoolsResponse>('/api/draft-pools');
  },

  /**
   * Create a draft pool (borrower only); payload has optional data fields
   */
  async createDraftPool(payload: UpsertDraftPoolPayload) {
    return apiClient<DraftPoolListItem>('/api/draft-pools', {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Update a draft pool (borrower only); payload has optional data fields
   */
  async updateDraftPool(payload: UpsertDraftPoolPayload, id: number) {
    return apiClient<DraftPoolListItem>(`/api/draft-pools/${id}`, {
      method: 'PUT',
      body: payload,
    });
  },

  /**
   * Get pool detail by ID (borrower view)
   */
  async getPoolById(poolId: number) {
    return apiClient<GetPoolByIdResponse>(`/api/pools/${poolId}`);
  },

  /**
   * Create a new pool
   */
  async createPool(poolData: CreatePoolPayload) {
    return apiClient<PoolDetail>('/api/pools', {
      method: 'POST',
      body: poolData,
    });
  },

  /**
   * Update a pool
   */
  async updatePool(poolId: number, updateData: Omit<UpdatePoolPayload, 'pool_id'>) {
    return apiClient<PoolDetail>(`/api/pools/${poolId}`, {
      method: 'PUT',
      body: updateData,
    });
  },

  /**
   * Delete a pool
   */
  async deletePool(poolId: number) {
    return apiClient<void>(`/api/pools/${poolId}`, {
      method: 'DELETE',
    });
  },

  async sendOffer(offerData: OfferPayload) {
    return apiClient<void>(`/api/offers`, {
      method: 'POST',
      body: offerData,
    });
  },

  async respondToOffer(offerData: RespondToOfferPayload) {
    return apiClient<void>(`/api/offers/${offerData.offer_id}`, {
      method: 'PUT',
      body: offerData,
    });
  },

  async deleteOffer(offerData: DeleteOfferPayload) {
    return apiClient<void>(`/api/offers/${offerData.offer_id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get overview stats for the authenticated user (borrower or investor)
   */
  async getOverview() {
    return apiClient<GetOverviewResponse>('/api/pools/overview');
  },

  /**
   * Add pool to favorites (investor only). POST /api/pools/favorites/<pool_id>
   */
  async addFavoritePool(poolId: number, isFavorite: boolean) {
    return apiClient<AddFavoritePoolResponse>(`/api/pools/${poolId}/favorite`, {
      method: 'POST',
      body: {
        is_favorite: isFavorite,
      },
    });
  },
};
